import React, { useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SectionHeader } from "@/components/common/Widgets";
import { fmtDate } from "@/lib/format";
import { Upload, FileText, Trash2, Download } from "lucide-react";

const CATEGORIES = ["KYC", "FINANCIAL", "BANK_STATEMENT", "GST_RETURN", "INVOICE", "STOCK_STATEMENT", "SALARY_SLIP", "ITR", "FORM_16", "EMPLOYMENT_PROOF", "PROPERTY_DOC", "RENT_AGREEMENT", "CO_APPLICANT_KYC", "CO_APPLICANT_INCOME", "COLLATERAL", "INSURANCE", "PHOTO", "VIDEO", "OTHER"];

export default function DocumentsSection({ app, docs, reload }) {
  const fileRef = useRef(null);
  const [category, setCategory] = useState("FINANCIAL");
  const [tag, setTag] = useState("");
  const [busy, setBusy] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("application_id", app.id);
      fd.append("category", category);
      if (tag) fd.append("tag", tag);
      fd.append("file", file);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      reload && reload();
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); reload && reload(); } catch (e) { toast.error("Delete failed"); }
  };

  const download = async (d) => {
    const token = localStorage.getItem("acas_token");
    try {
      const res = await fetch(`${BACKEND_URL}/api/documents/${d.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = d.filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-3">
      <SectionHeader title="Documents (DMS)" subtitle="All documents are auto-indexed by SHA-256 hash for integrity." />
      <Card className="p-4 space-y-3">
        <div className="flex flex-col md:flex-row gap-2">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="md:w-[200px]" data-testid="docs-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input className="md:w-[220px]" placeholder="Tag (optional)" value={tag} onChange={(e) => setTag(e.target.value)} />
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.xlsx,.csv,.txt" onChange={onUpload} className="hidden" data-testid="docs-upload-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="docs-upload-button"><Upload className="h-4 w-4 mr-1.5" />{busy ? "Uploading…" : "Upload Document"}</Button>
        </div>
        <div className="text-xs text-muted-foreground">Accepted: PDF, PNG, JPG, JPEG, XLSX, CSV, TXT (max 25 MB)</div>
      </Card>
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2.5">File</th>
                <th className="text-left px-3 py-2.5">Category</th>
                <th className="text-left px-3 py-2.5">Tag</th>
                <th className="text-right px-3 py-2.5">Size</th>
                <th className="text-left px-3 py-2.5">Uploaded</th>
                <th className="text-left px-3 py-2.5">SHA-256</th>
                <th className="text-right px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(docs || []).length === 0 && <tr><td colSpan={7} className="text-center py-10 text-sm text-muted-foreground"><FileText className="h-7 w-7 mx-auto mb-2 text-muted-foreground/60" />No documents uploaded yet.</td></tr>}
              {(docs || []).map((d) => (
                <tr key={d.id} className="border-t border-border hover:bg-muted/30">
                  <td className="px-3 py-2 font-medium">{d.filename}</td>
                  <td className="px-3 py-2"><Badge variant="secondary" className="text-xs">{d.category}</Badge></td>
                  <td className="px-3 py-2 text-xs">{d.tag || "—"}</td>
                  <td className="px-3 py-2 num-right">{Math.round((d.size || 0) / 1024)} KB</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground mono">{fmtDate(d.uploaded_at)}</td>
                  <td className="px-3 py-2 mono text-[10px] text-muted-foreground">{(d.sha256 || "").slice(0, 12)}…</td>
                  <td className="px-3 py-2 text-right space-x-1">
                    <Button variant="outline" size="sm" onClick={() => download(d)} data-testid={`docs-download-${d.id}`}><Download className="h-3.5 w-3.5" /></Button>
                    <Button variant="outline" size="sm" onClick={() => remove(d.id)} data-testid={`docs-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
